import React, { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'

interface TooltipProps {
  text: string
  children: React.ReactElement
  delay?: number
  placement?: 'top' | 'bottom'
}

export default function Tooltip({ text, children, delay = 400, placement = 'top' }: TooltipProps) {
  const [visible, setVisible] = useState(false)
  const [coords, setCoords] = useState({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLElement | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const show = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      if (!triggerRef.current) return
      const rect = triggerRef.current.getBoundingClientRect()
      setCoords({
        top: placement === 'top'
          ? rect.top + window.scrollY - 8
          : rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX + rect.width / 2,
      })
      setVisible(true)
    }, delay)
  }, [delay, placement])

  const hide = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    setVisible(false)
  }, [])

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current)
  }, [])

  const cloned = React.cloneElement(children, {
    ref: (el: HTMLElement | null) => {
      triggerRef.current = el
      const existingRef = (children as any).ref
      if (typeof existingRef === 'function') existingRef(el)
      else if (existingRef && 'current' in existingRef) existingRef.current = el
    },
    onMouseEnter: (e: React.MouseEvent) => {
      show()
      children.props.onMouseEnter?.(e)
    },
    onMouseLeave: (e: React.MouseEvent) => {
      hide()
      children.props.onMouseLeave?.(e)
    },
  })

  return (
    <>
      {cloned}
      {visible && createPortal(
        <div style={{
          position: 'fixed',
          top: coords.top,
          left: coords.left,
          transform: placement === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          color: 'var(--text-secondary)',
          fontSize: '0.72rem',
          fontWeight: 500,
          padding: '0.25rem 0.6rem',
          borderRadius: '6px',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          zIndex: 99999,
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          animation: 'ttFade 0.12s ease',
        }}>
          {text}
          <style>{`@keyframes ttFade { from { opacity:0 } to { opacity:1 } }`}</style>
        </div>,
        document.body
      )}
    </>
  )
}
